import {
  Button,
  Checkbox,
  Input,
  Menu,
  MenuItem,
  Modal,
  notify,
} from '@affine/component';
import { FetchService } from '@affine/core/modules/cloud';
import type {
  DialogComponentProps,
  GLOBAL_DIALOG_SCHEMA,
} from '@affine/core/modules/dialogs';
import { sanitizeFeedbackPrefill } from '@affine/core/modules/help-actions';
import { WorkspaceService } from '@affine/core/modules/workspace';
import { useI18n } from '@affine/i18n';
import { useService, useServiceOptional } from '@toeverything/infra';
import { useMemo, useState } from 'react';

import { buildFeedbackPayload } from './payload';
import * as styles from './styles.css';

export const FeedbackDialog = ({
  close,
  type: initialType,
  prefill,
}: DialogComponentProps<GLOBAL_DIALOG_SCHEMA['feedback']>) => {
  const t = useI18n();
  const fetchService = useService(FetchService);
  const workspaceService = useServiceOptional(WorkspaceService);
  const [type, setType] = useState<'bug' | 'idea'>(initialType);
  const [title, setTitle] = useState(
    sanitizeFeedbackPrefill(prefill?.title, 120) ?? ''
  );
  const [description, setDescription] = useState(
    sanitizeFeedbackPrefill(prefill?.description, 5_000) ?? ''
  );
  const [includeDiagnostics, setIncludeDiagnostics] = useState(
    Boolean(prefill?.errorStack)
  );
  const [loading, setLoading] = useState(false);
  const [error, setError] = useState<string>();
  const payload = useMemo(
    () =>
      buildFeedbackPayload({
        type,
        title,
        description,
        workspaceId: workspaceService?.workspace.id,
        includeDiagnostics,
        pathname: location.pathname,
        appVersion: BUILD_CONFIG.appVersion,
        userAgent: navigator.userAgent,
        errorStack: prefill?.errorStack,
      }),
    [
      description,
      includeDiagnostics,
      prefill?.errorStack,
      title,
      type,
      workspaceService?.workspace.id,
    ]
  );
  const diagnostics = useMemo(
    () => ({
      routePath: 'routePath' in payload ? payload.routePath : undefined,
      appVersion: 'appVersion' in payload ? payload.appVersion : undefined,
      userAgent: 'userAgent' in payload ? payload.userAgent : undefined,
      errorStack: 'errorStack' in payload ? payload.errorStack : undefined,
    }),
    [payload]
  );
  const canSubmit = title.trim().length >= 3 && description.trim().length >= 10;
  const typeLabel =
    type === 'bug'
      ? t['com.streamlain.feedback.type.bug']()
      : t['com.streamlain.feedback.type.idea']();

  const submit = async () => {
    if (loading || title.trim().length < 3 || description.trim().length < 10) {
      return;
    }
    setLoading(true);
    setError(undefined);
    try {
      const response = await fetchService.fetch('/api/feedback', {
        method: 'POST',
        headers: { 'content-type': 'application/json' },
        body: JSON.stringify(payload),
      });
      if (!response.ok) throw new Error('feedback_submit_failed');
      notify.success({
        title: t['com.streamlain.feedback.sent'](),
      });
      close();
    } catch {
      setError(t['com.streamlain.feedback.failed']());
    } finally {
      setLoading(false);
    }
  };

  return (
    <Modal
      open
      onOpenChange={open => {
        if (!open && !loading) close();
      }}
      width={520}
      contentOptions={{
        ['data-testid' as string]: 'feedback-dialog',
        style: { padding: 24, overflow: 'hidden' },
      }}
    >
      <form
        onSubmit={event => {
          event.preventDefault();
          void submit();
        }}
        className={styles.form}
      >
        <h2 className={styles.heading}>
          {t['com.streamlain.feedback.title']()}
        </h2>
        <div className={styles.fields}>
          <label className={styles.field}>
            <span className={styles.fieldLabel}>
              {t['com.streamlain.feedback.type']()}
            </span>
            <Menu
              contentOptions={{ align: 'start', side: 'bottom' }}
              items={
                <>
                  <MenuItem onClick={() => setType('bug')}>
                    {t['com.streamlain.feedback.type.bug']()}
                  </MenuItem>
                  <MenuItem onClick={() => setType('idea')}>
                    {t['com.streamlain.feedback.type.idea']()}
                  </MenuItem>
                </>
              }
            >
              <Button type="button" className={styles.typeTrigger}>
                {typeLabel}
              </Button>
            </Menu>
          </label>
          <label className={styles.field}>
            <span className={styles.fieldLabel}>
              {t['com.streamlain.feedback.field.title']()}
            </span>
            <Input
              required
              minLength={3}
              maxLength={120}
              value={title}
              onChange={setTitle}
            />
          </label>
          <label className={styles.field}>
            <span className={styles.fieldLabel}>
              {t['com.streamlain.feedback.description']()}
            </span>
            <textarea
              className={styles.textArea}
              required
              minLength={10}
              maxLength={5000}
              rows={5}
              value={description}
              onChange={event => setDescription(event.target.value)}
            />
          </label>
          <div className={styles.diagnosticsPanel}>
            <p className={styles.diagnosticsNotice}>
              {t['com.streamlain.feedback.diagnostics-notice']()}
            </p>
            <label className={styles.diagnosticsToggle}>
              <Checkbox
                checked={includeDiagnostics}
                onChange={(_, checked) => setIncludeDiagnostics(checked)}
              />
              {t['com.streamlain.feedback.include-diagnostics']()}
            </label>
            {includeDiagnostics ? (
              <details className={styles.diagnosticsDetails}>
                <summary>{t['com.streamlain.feedback.preview']()}</summary>
                <pre className={styles.diagnosticsCode}>
                  {JSON.stringify(diagnostics, null, 2)}
                </pre>
              </details>
            ) : null}
          </div>
          {error ? (
            <p className={styles.error} role="alert">
              {error}
            </p>
          ) : null}
        </div>
        <div className={styles.actions}>
          <Button type="button" disabled={loading} onClick={() => close()}>
            {t['com.streamlain.feedback.cancel']()}
          </Button>
          <Button
            type="submit"
            variant="primary"
            loading={loading}
            disabled={!canSubmit || loading}
          >
            {t['com.streamlain.feedback.send']()}
          </Button>
        </div>
      </form>
    </Modal>
  );
};
